import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { AuthStore } from './auth.store';

/**
 * Interceptor universal — anexa Bearer + lida com 401 via refresh single-flight.
 *
 * Aplicado a TODOS os requests HTTP (REST + Apollo, que usa HttpClient via
 * `apollo-angular/http`).
 *
 * Skip: requests para `/auth/refresh` e `/auth/logout` não recebem Bearer
 * (refresh usa cookie, logout não precisa). Em 401 nesses, NÃO tenta refresh
 * (causaria loop).
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const store = inject(AuthStore);
  const auth = inject(AuthService);
  const router = inject(Router);

  const isRefreshOrLogout =
    req.url === `${environment.apiUrl}/auth/refresh` ||
    req.url === `${environment.apiUrl}/auth/logout`;

  // Anexa Bearer se houver sessionToken e não for endpoint de auth.
  const token = store.sessionToken();
  const authReq =
    token && !isRefreshOrLogout
      ? req.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        })
      : req.clone({ withCredentials: true });

  return next(authReq).pipe(
    catchError((err: unknown) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        !isRefreshOrLogout
      ) {
        // Tenta refresh single-flight e replay do request original.
        return auth.refresh().pipe(
          switchMap((res) => {
            const retried = req.clone({
              setHeaders: { Authorization: `Bearer ${res.sessionToken}` },
              withCredentials: true,
            });
            return next(retried);
          }),
          catchError((refreshErr: unknown) => {
            // Refresh falhou — sessão é genuinamente inválida.
            void router.navigate(['/auth/login']);
            return throwError(() => refreshErr);
          }),
        );
      }
      return throwError(() => err);
    }),
  );
};
